import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import type {SourceMaterialSerializer} from "@/generated/DjangoTypes";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import type {TCarouselMedia} from "@/store/carousel";

const STALE_TIME = 5 * 60 * 1000;

/**
 * Complete a source media into its application's whole album.
 *
 * A source buyout is granted per application, so the carousel has to show -- and buy -- every raw
 * file of that application. The gallery and the media library only hold the page they loaded, so
 * the set is fetched instead of assembled from those rows. Until it arrives, whatever the caller
 * grouped from the loaded media is shown unchanged.
 */
export const useSourceAlbum = ({content}: {content?: TCarouselMedia}): TCarouselMedia | undefined => {
  const applicationId = content?.applicationId;
  const isSourceContent = content?.instagram_media?.media_product_type === "SOURCE";

  const {data: sourceMaterials} = useGenericQuery<SourceMaterialSerializer[]>({
    queryKey: CAMPAIGN_APPLICATION_KEYS.qApplicationSourceMaterials(applicationId!),
    enabled: !!isSourceContent && !!applicationId,
    staleTime: STALE_TIME,
  });

  if (!content || !sourceMaterials?.length || !isSourceContent) {
    return content;
  }

  return {
    ...content,
    instagram_media: {
      ...content.instagram_media!,
      children: sourceMaterials.map(sourceMaterial => ({
        ...sourceMaterial,
        media_id: "",
        media_product_type: "SOURCE" as const,
        media_type: (sourceMaterial.thumbnail_file ? "VIDEO" : "IMAGE") as "VIDEO" | "IMAGE",
      })),
      media_type: sourceMaterials.length > 1 ? "CAROUSEL_ALBUM" : content.instagram_media!.media_type,
      purchased: sourceMaterials.every(sourceMaterial => !!sourceMaterial.purchased)
        ? (sourceMaterials[0].purchased ?? content.instagram_media?.purchased)
        : null,
    },
  };
};
